import React, {useMemo, useState} from "react";
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonItem,
  IonLoading,
  IonPage,
  IonPopover,
  IonRow,
  IonText,
  IonToast
} from "@ionic/react";
import Header from "../components/Header";
import {ROLE} from "../../enums/ROLE";
import {AuthContext} from "../../providers/authContext";
import {calculatorOutline} from "ionicons/icons";
import {Calendar, CalendarValueType} from "primereact/calendar";
import yoneticiService from "../../services/YoneticiService";
import {formatLocalDate, logError, parseTurkishNumber} from "../../utils";
import './YoneticiMukellefBorcSorgulama.css';
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {Button} from "primereact/button";
import {BorcSorgulama} from "../entities/BorcSorgulama";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Mukellef} from "../entities/Mukellef";
import {ColumnGroup} from "primereact/columngroup";
import {Row} from "primereact/row";

const YoneticiMukellefBorcSorgulama: React.FC = () => {
  const {kurumBilgileri, logo} = React.useContext(AuthContext);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [showToastMessage, setShowToastMessage] = useState<string>();
  const [adiSoyadi, setAdiSoyadi] = useState<string>();
  const [vadeTarihi, setVadeTarihi] = useState<CalendarValueType>();
  const [borcSorgulama, setBorcSorgulama] = useState<BorcSorgulama>();
  const [sicilListesi, setSicilListesi] = useState<Mukellef[]>([]);
  const [sicilListesiPopoverOpen, setSicilListesiPopoverOpen] = useState<boolean>(false);
  const sicilListesiPopover = React.createRef<HTMLIonPopoverElement>();
  const [popoverEvent, setPopoverEvent] = useState<MouseEvent | undefined>(undefined);

  const fetchData = (borcSorgulama: BorcSorgulama) => {
    setShowLoading(true);
    yoneticiService
      .getMukellefBorcListesi(borcSorgulama)
      .then((data: BorcSorgulama) => {
        setBorcSorgulama(data);
        setAdiSoyadi(data.adiSoyadi!);
      })
      .catch((error: any) => {
        logError(error);
        setShowToastMessage(
          error.response ? error.response.data.message : error
        );
      })
      .finally(() => setShowLoading(false));
  }

  const sicilAra = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!borcSorgulama?.adiSoyadi) {
      setShowToastMessage("Adı Soyadı boş olamaz");
      return;
    }
    setShowLoading(true);
    yoneticiService.getSicilListesi(borcSorgulama?.adiSoyadi).then((data: Mukellef[]) => {
      setSicilListesi(data);
      setPopoverEvent(e.nativeEvent);   // butonun click eventini kaydet
      setSicilListesiPopoverOpen(true);
    }).catch((error: any) => {
      logError(error);
      setShowToastMessage(
        error.response ? error.response.data.message : error
      );
    }).finally(() => setShowLoading(false));
  }

  const tableHeader = useMemo(() => (
    <IonItem>
      <IonText color="danger"><strong>ADI SOYADI: {adiSoyadi}</strong></IonText>
    </IonItem>
  ), [adiSoyadi]);

  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column footer="GENEL TOPLAM"/>
        <Column footerStyle={{textAlign: 'right'}}
          footer={borcSorgulama?.borcListesi?.reduce((acc, curr) => acc + (parseTurkishNumber(curr.tutar) || 0), 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
        />
      </Row>
    </ColumnGroup>
  );

  return (
    <IonPage>
      <Header
        title={kurumBilgileri?.kisaTanim}
        subtitle="Mükellef Borç Sorgulama"
        logo={logo}
        roles={[ROLE.Yonetici]}
      />
      <IonContent>
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          message={"Lütfen Bekleyin..."}
        />
        <IonToast
          isOpen={showToastMessage !== undefined}
          onDidDismiss={() => setShowToastMessage(undefined)}
          message={showToastMessage}
          duration={5000}
          position="top"
        />
        <IonCard>
          <IonCardHeader>
            <IonItem color="light" className="xion-padding-bottom">
              <IonIcon icon={calculatorOutline} slot="start"/>
              Borç Sorgulama
            </IonItem>
            <IonItem color="light" lines="none">
              <IonGrid>
                <IonRow className="ion-align-items-center">
                  <IonCol size="5" sizeSm="3">
                    <IonText>Sicil / T.C. No</IonText>
                  </IonCol>
                  <IonCol size="5" sizeSm="3">
                    <IonText>Abone No</IonText>
                  </IonCol>
                </IonRow>
                <IonRow className="ion-align-items-center">
                  <IonCol size="5" sizeSm="3">
                    <InputNumber value={borcSorgulama?.sicilNo}
                                 onChange={(e) => {
                                   setBorcSorgulama({...borcSorgulama, sicilNo: e.value!});
                                 }}
                                 onBlur={(e) => {
                                   const sicilNo = Number(e.target.value);
                                   if (sicilNo === 0) return;
                                   fetchData({
                                     ...borcSorgulama,
                                     aboneNo: null,
                                     adiSoyadi: null,
                                     sicilNo: sicilNo
                                   });
                                 }}
                                 format={false} maxLength={11} min={0} inputMode="numeric" useGrouping={false} size={10}
                    />
                  </IonCol>
                  <IonCol size="5" sizeSm="3">
                    <InputNumber value={borcSorgulama?.aboneNo}
                                 onChange={(e) => {
                                   setBorcSorgulama({...borcSorgulama, aboneNo: e.value!});
                                 }}
                                 onBlur={(e) => {
                                   const aboneNo = Number(e.target.value);
                                   if (aboneNo === 0) return;
                                   fetchData({
                                     ...borcSorgulama,
                                     aboneNo: aboneNo,
                                     adiSoyadi: null,
                                     sicilNo: null
                                   });
                                 }}
                                 format={false} maxLength={11} min={0} inputMode="numeric" useGrouping={false} size={13}
                    />
                  </IonCol>
                </IonRow>
                <IonRow className="ion-align-items-center">
                  <IonCol size="5" sizeSm="3">
                    <IonText>Adı Soyadı</IonText>
                  </IonCol>

                  <IonCol size="7" sizeSm="6">
                    <div className="p-inputgroup">
                      <InputText id="sicil-input" value={borcSorgulama?.adiSoyadi ?? ""}
                                 onChange={(e) => setBorcSorgulama({
                                   ...borcSorgulama,
                                   adiSoyadi: e.target.value,
                                   sicilNo: null,
                                   aboneNo: null
                                 })}/>
                      <Button label="Ara" onClick={(e) => sicilAra(e)}/>
                    </div>
                    <IonPopover showBackdrop={false} ref={sicilListesiPopover} isOpen={sicilListesiPopoverOpen}
                                onDidDismiss={() => setSicilListesiPopoverOpen(false)}
                                event={popoverEvent}   // konum için gerekli
                                side="bottom"           // alt tarafa açılmasını sağlar
                                alignment="start"       // sola hizalar
                                className="sicil-popover">
                      <IonContent class="ion-padding">
                        <DataTable value={sicilListesi} stripedRows className="p-datatable-sm sicil-table"
                                   scrollable scrollDirection="horizontal" style={{width: '100%'}}
                                   emptyMessage="Tabloda herhangi bir veri mevcut değil"
                                   onRowClick={(e) => {
                                     fetchData({
                                       ...borcSorgulama,
                                       aboneNo: null,
                                       adiSoyadi: null,
                                       sicilNo: e.data.sicilNo
                                     });
                                     setSicilListesiPopoverOpen(false);
                                     setPopoverEvent(undefined);
                                   }}>
                          <Column field="adiSoyadiUnvan" header="Adı Soyadı/Unvanı"/>
                          <Column field="babaAdi" header="Baba Adı"/>
                          <Column field="sicilNo" header="Sicil No"/>
                        </DataTable>
                      </IonContent>
                    </IonPopover>
                  </IonCol>
                </IonRow>
                <IonRow className="ion-align-items-center">
                  <IonCol size="5" sizeSm="3">
                    <IonText>Vade Tarihi</IonText>
                  </IonCol>

                  <IonCol size="6" sizeSm="4">
                    <Calendar id="vadeTarihi" locale="tr"
                              dateFormat="dd.mm.yy"
                              value={vadeTarihi}
                              onChange={(e) => {
                                setVadeTarihi(e.value);
                                setBorcSorgulama({...borcSorgulama, vadeTarihi: formatLocalDate(e.value as Date)});
                                if (!borcSorgulama?.sicilNo || !borcSorgulama?.aboneNo || !borcSorgulama?.adiSoyadi) return;
                                fetchData({...borcSorgulama, vadeTarihi: formatLocalDate(e.value as Date)});
                              }} showIcon showButtonBar/>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonItem>
          </IonCardHeader>
          <IonCardContent>
            <DataTable value={borcSorgulama?.borcListesi} stripedRows className="p-datatable-sm"
                       scrollable
                       scrollDirection="horizontal"
                       style={{minWidth: '100%'}}
                       emptyMessage="Tabloda herhangi bir veri mevcut değil"
                       header={tableHeader}
                       footerColumnGroup={footerGroup}
            >
              <Column field="tur" header="TÜR"/>
              <Column field="tutar" header="TOPLAM TUTAR" bodyClassName="ion-justify-content-end"/>
            </DataTable>
          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  )
};

export default YoneticiMukellefBorcSorgulama;
